<!-- src/pages/purchase/ticket-detail.vue -->
<script setup>
import { nextTick, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import articleApi from '@/api/ArticleApi';
import { patchArticleLike } from '@/api/EowayApi';
import { usePaymentsStore } from '@/store/modules/payments';
import { getPathnameFromHref } from '@/composables/linkInterceptors';
import { useRnBridge } from '@/composables/useRnBridge';

import { useDialogManager } from '@/composables/useDialogManager';
import IconGift from '@/assets/images_manse/icon_gift.svg';
import IconArrow03 from '@/components/svg/IconArrow03.vue';
import IconArrow04 from '@/components/svg/IconArrow04.vue';
import PopPurchase from '@/components/purchase/PopPurchase.vue';
import PopGifting from '@/components/purchase/PopGifting.vue';
import ConfirmComponent from '@/components/common/Confirm.vue';
import TicketProductGuide from '@/components/purchase/TicketProductGuide.vue';
import { getTicketGuideConfig } from '@/components/purchase/ticketGuideData';
import { getRouteByAnalysisTypeId, getAnalysisTypeIdFromProduct } from '@/utils/analysisRouteMap';
import { getNextStepAPI } from '@/api/PaymentApi';
import { usePrimarySajuStore } from '@/store/modules/primarySaju';

definePage({ meta: { layout: 'purchase', title: '서비스구매' } });

const route = useRoute();
const router = useRouter();

const store = useStore();
const paymentsStore = usePaymentsStore();

const primarySajuStore = usePrimarySajuStore();
const { sendMessageToApp } = useRnBridge();
const accessToken = ref(sessionStorage.getItem('X-Token') || '');
const {
  dialogTitle,
  dialogTitleClass,
  dialogTitle2,
  dialogTitle2Class,
  dialogMessage,
  dialogText,
  dialogNextUrl,
  dialogWarning,
  confirmRef,
  showConfirm,
} = useDialogManager();

const boardId = ref(null);
const articleId = ref(null);

const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024);

const ticketData = ref(null);
const likeData = ref({ likeCnt: null, likeClicked: false });
const selectedOption = ref(null);

const keywords = ref(null);

const prevData = ref(null);
const optionItems = ref([]);

const purchasePopupRef = ref(null);
const giftingPopupRef = ref(null);

const totalBasePrice = ref(0);
const totalPrice = ref(0);
const isOptionEmpty = ref(false);

const userId = ref(null);
userId.value = store.getters['user/id'];

onMounted(async () => {
  articleId.value = route.params?.id;
  boardId.value = route.params?.boardId;
  await getView();
  getPrev();

  // 쿼리 파라미터에서 optionId 복원 (충전 후 돌아왔을 때)
  if (route.query.optionId) {
    const optionId = Number(route.query.optionId);
    const optionExists = optionItems.value.some((opt) => opt.value === optionId);
    if (optionExists) {
      selectedOption.value = optionId;
      totalBasePrice.value = productBasePrice.value + optionBasePrice.value;
      totalPrice.value = productPrice.value + optionPrice.value;
    }
  } else if (!isOptionEmpty.value) {
    // optionId 없고 옵션이 있는 상품이면 초기화 (처음 진입한 것과 동일하게)
    selectedOption.value = null;
    totalBasePrice.value = 0;
    totalPrice.value = 0;
  }

  window.addEventListener('resize', handleResize);
});

const handleResize = () => {
  windowWidth.value = window.innerWidth;
};

const getView = async () => {
  try {
    const result = await articleApi().get(boardId.value, articleId.value, {});

    if (!result) {
      router.back();
      return;
    }

    ticketData.value = result?.data;
    keywords.value = result?.data.keywords;

    likeData.value.likeCnt = result?.data?.likeCnt;
    likeData.value.likeClicked = result?.data?.likeChecked;
    const options = ticketData.value?.product?.optionGroups?.[0]?.options ?? [];
    if (options.length === 0) {
      isOptionEmpty.value = true;
      totalBasePrice.value = productBasePrice.value;
      totalPrice.value = productPrice.value;
    } else {
      optionItems.value = options.map((opt) => ({
        title: trimItemTitle(opt.name),
        value: opt.optionId,
        disabled: opt.disabled,
      }));
    }
  } catch (error) {
    alert(error);
  }
};

const getPrev = async () => {
  try {
    const result = await articleApi().getPrev(articleId.value, { boardId: boardId.value });

    if (!result) {
      router.back();
      return;
    }

    prevData.value = result;
  } catch (error) {
    alert(error);
  }
};

const goViewPrevNext = (boardId, articleId) => {
  window.location.href = `/purchase/ticket-detail/${boardId}/${articleId}`;
};

const tabs = [
  { label: '서비스 설명', value: 'tab1' },
  { label: '추천', value: 'tab2' },
  { label: '예시 화면', value: 'tab3' },
];

const currentTab = ref(tabs[0].value);

const toggleSelectedClass = (isSelected) => {
  document.querySelectorAll('.btn-tab-guide').forEach((button) => {
    button.classList.toggle('selected', isSelected && button.dataset.value === currentTab.value);
  });

  const tabGuideList = document.querySelector('.tab-guide-list');
  if (tabGuideList) {
    tabGuideList.classList.toggle('selected', isSelected);
  }

  const tabGuideContent = document.querySelector('.guide-pro-cont');
  if (tabGuideContent) {
    tabGuideContent.classList.toggle('selected', isSelected);
  }
};

const scrollToSection = (sectionId) => {
  const section = document.getElementById(sectionId);
  if (section) {
    section.scrollIntoView({ behavior: 'smooth' });
    currentTab.value = sectionId;
    //toggleSelectedClass(true)
  }
};

// 스크롤 이벤트 리스너 추가
window.addEventListener('scroll', () => {
  const scrollPosition = window.scrollY; // 현재 스크롤 위치
  const section = document.getElementById(currentTab.value); // 현재 탭에 해당하는 섹션

  if (section) {
    const sectionTop = section.getBoundingClientRect().top + window.scrollY; // 섹션의 상단 위치

    if (scrollPosition < sectionTop - 200) {
      toggleSelectedClass(false);
    } else if (scrollPosition >= sectionTop + 200) {
      toggleSelectedClass(true);
    }
  }
});

/** 좋아요 버튼 클릭 */
const patchLike = async () => {
  try {
    const result = await patchArticleLike(articleId.value);
    likeData.value = result;
  } catch (error) {}
};

const goViewList = () => {
  router.push({
    path: `/purchase/tickets`,
    query: {
      // 쿼리 파라미터 추가
      //tabCate: tabCate.value,
    },
  });
};
const items = ['소장용', '소장용'];

/** 숫자 천단위 콤마 포맷팅 */
const formatNumber = (number) => {
  return number?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const openPurchasePopup = () => {
  if (!userId.value && !accessToken.value) {
    showConfirm({
      title: '로그인 후 이용 가능합니다.',
      title2: '로그인 페이지로 이동하시겠습니까?',
      text: '확인',
      onConfirm: () => confirmLogin(),
    });
  } else {
    purchasePopupRef.value?.open();
  }
};

const confirmLogin = () => {
  if (window.ReactNativeWebView) {
    sendMessageToApp({ action: 'login' });
  } else {
    router.push({ path: '/auth/login', query: { to: `${route.path}` } });
  }
};

const openGiftingPopup = () => {
  if (!userId.value && !accessToken.value) {
    showConfirm({
      title: '로그인 후 이용 가능합니다.',
      title2: '로그인 페이지로 이동하시겠습니까?',
      text: '확인',
      onConfirm: () => confirmLogin(),
    });
  } else {
    giftingPopupRef.value?.open();
  }
};

const discountRate = computed(() => {
  const original = totalBasePrice.value;
  const current = totalPrice.value;

  if (original === 0 || current >= original) {
    return 0;
  }

  const rate = Math.round(((original - current) / original) * 100);
  return rate;
});

const isPurchaseEnabled = computed(() => {
  return totalPrice.value > 0;
});

/** 셀렉트박스 관련 로직 */
const showDropdown = ref(false);
const selectWrapperRef = ref(null);
const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value;
};
onClickOutside(selectWrapperRef, () => {
  showDropdown.value = false;
});
const selectOption = (option) => {
  if (!option.disabled) {
    selectedOption.value = option.value;
    showDropdown.value = false;

    totalBasePrice.value = productBasePrice.value + optionBasePrice.value;
    totalPrice.value = productPrice.value + optionPrice.value;
  }
};
const selectedOptionLabel = computed(() => {
  const selected = optionItems.value.find((opt) => opt.value === selectedOption.value);
  return selected ? selected.title : '옵션 선택';
});
const trimItemTitle = (name) => {
  const [main, tail] = name.split('+').map((part) => part.trim());
  if (tail === '0 P') return main;
  return name;
};

const priceMode = computed(() => {
  return ticketData.value?.product?.pointPrice != null ? 'pointPrice' : 'price';
});

const productBasePrice = computed(() => {
  if (priceMode.value === 'pointPrice') {
    return ticketData.value?.product?.basePointPrice || 0;
  } else {
    return ticketData.value?.product?.basePrice || 0;
  }
});

const optionBasePrice = computed(() => {
  if (selectedOptionData.value === null) return 0;

  const optionData = ticketData.value?.product?.optionGroups
    ?.flatMap((group) => group.options)
    .find((opt) => opt.optionId === selectedOptionData.value.optionId);

  if (priceMode.value === 'pointPrice') {
    return optionData?.basePointPrice || 0;
  } else {
    return optionData?.basePrice || 0;
  }
});

const productPrice = computed(() => {
  if (priceMode.value === 'pointPrice') {
    return ticketData.value?.product?.pointPrice || 0;
  } else {
    return ticketData.value?.product?.price || 0;
  }
});

const optionPrice = computed(() => {
  if (selectedOptionData.value === null) return 0;

  const optionData = ticketData.value?.product?.optionGroups
    ?.flatMap((group) => group.options)
    .find((opt) => opt.optionId === selectedOptionData.value.optionId);

  if (priceMode.value === 'pointPrice') {
    return optionData?.pointPrice || 0;
  } else {
    return optionData?.price || 0;
  }
});

const articleHtml = computed(() => {
  const isRN = typeof window !== 'undefined' && window.ReactNativeWebView;
  const isMobileWidth = windowWidth.value <= 768;
  if (isRN || isMobileWidth) {
    return ticketData?.value?.articleCtnM;
  } else {
    return ticketData?.value?.articleCtn;
  }
});

// 상품 가이드 설정
// - 단일 타입: analysisTypeId 그대로 키 사용
// - 패키지(여러 타입): 정렬 후 콤마 결합 키 사용 (예: '38,41')
const guideAnalysisTypeId = computed(() => {
  const details = ticketData.value?.product?.detail;
  if (!details?.length) return null;
  const ids = [...new Set(details.map((d) => d.analysisTypeId))].sort((a, b) => a - b);
  return ids.length === 1 ? ids[0] : ids.join(',');
});

const guideConfig = computed(() => {
  if (guideAnalysisTypeId.value === null) return null;
  return getTicketGuideConfig(guideAnalysisTypeId.value);
});

// 낭만궁합 1:1 분석권 여부 (product.detail[].memberCount === 2)
// 1:1 = 두 사람 궁합 → memberCount 2, 1:2 → 3, 1:3 → 4
const isHarmony1to1 = computed(() => {
  const details = ticketData.value?.product?.detail;
  if (!details?.length) return false;
  return details.some((d) => d.memberCount === 2);
});

// 상단(상세설명 위) 가이드: 낭만궁합(38)은 제외
const topGuideConfig = computed(() => {
  if (!guideConfig.value) return null;
  if (guideAnalysisTypeId.value === 38) return null;
  return guideConfig.value;
});

// 하단(상세설명 아래) 가이드: 낭만궁합 1:1 분석권에서만 노출
const bottomGuideConfig = computed(() => {
  if (!guideConfig.value) return null;
  if (guideAnalysisTypeId.value !== 38) return null;
  if (!isHarmony1to1.value) return null;
  return guideConfig.value;
});

const selectedOptionData = computed(() => {
  const selected = optionItems.value.find((opt) => opt.value === selectedOption.value);
  if (!selected) return null;

  const optionGroup = ticketData.value?.product?.optionGroups?.find((group) =>
    group.options.some((opt) => opt.optionId === selected.value),
  );

  if (!optionGroup) return null;

  return {
    optionId: selected.value,
    optionGroupId: optionGroup.optionGroupId,
  };
});

/** 구매 성공 후 next-step API 기반 이동 팝업 */
const onPurchaseSuccess = async () => {
  const analysisTypeId = getAnalysisTypeIdFromProduct(ticketData.value?.product);
  if (!analysisTypeId) return;

  try {
    const nextStepData = await getNextStepAPI(analysisTypeId);
    const { nextStep, guestUserId, message } = nextStepData;

    if (nextStep === '상세분석') {
      // 낭만궁합(38): guestUserId를 query로 전달하여 본인 사주 자동 설정
      if (analysisTypeId === 38) {
        const harmonyGuestUserId = guestUserId || primarySajuStore.mySajuId;
        const isMobile = window.ReactNativeWebView || windowWidth.value <= 768;
        const harmonyPath = isMobile ? '/m/saju-insight/nangman-harmony' : '/saju-service';
        const harmonyRoute = isMobile
          ? { path: harmonyPath, query: { id: harmonyGuestUserId || undefined } }
          : { path: harmonyPath, query: { menu: '/overviewNangmanHarmony' } };
        showConfirm({
          title: '구매가 완료되었습니다.',
          message,
          title2: '낭만궁합으로 이동할까요?',
          title2Class: 'confirm-highlight-orange',
          text: '이동',
          onConfirm: () => router.push(harmonyRoute),
        });
        return;
      }

      // 낭만월운(10): 이동 직후 월 선택 구매 팝업 자동 노출
      if (analysisTypeId === 10) {
        const monthlyGuestUserId = guestUserId || primarySajuStore.mySajuId;
        const isMobile = window.ReactNativeWebView || windowWidth.value <= 960;
        const monthlyRoute = isMobile
          ? {
              path: '/m/saju-insight/monthly-fortune',
              query: { id: monthlyGuestUserId, openPurchase: '1' },
            }
          : {
              path: `/analysis/${monthlyGuestUserId}`,
              query: { menu: '/overviewMonthlyFortune', openPurchase: '1' },
            };
        showConfirm({
          title: '구매가 완료되었습니다.',
          message,
          title2: '낭만월운으로 이동할까요?',
          title2Class: 'confirm-highlight-orange',
          text: '이동',
          onConfirm: () => router.push(monthlyRoute),
        });
        return;
      }

      // 낭만작명(47): 전용 페이지로 이동 (사주분석 /analysis 계열 아님)
      if (analysisTypeId === 47) {
        const namingGuestUserId = guestUserId || primarySajuStore.mySajuId;
        const isMobile = window.ReactNativeWebView || windowWidth.value <= 768;
        const namingPath = isMobile ? '/m/saju-insight/nangman-naming' : '/saju-service';
        const namingRoute = isMobile
          ? { path: namingPath, query: { id: namingGuestUserId || undefined } }
          : { path: namingPath, query: { menu: '/overviewNangmanNaming' } };
        showConfirm({
          title: '구매가 완료되었습니다.',
          message,
          title2: '낭만작명으로 이동할까요?',
          title2Class: 'confirm-highlight-orange',
          text: '이동',
          onConfirm: () => router.push(namingRoute),
        });
        return;
      }

      // 상세분석: 기존 팝업 동일, guestUserId 기반으로 이동
      const routeInfo = getRouteByAnalysisTypeId(analysisTypeId, {
        primarySajuId: guestUserId,
      });
      if (!routeInfo) return;

      showConfirm({
        title: '구매가 완료되었습니다.',
        title2: `${routeInfo.label} 페이지로 이동하시겠습니까?`,
        title2Class: 'confirm-highlight-orange',
        text: '이동',
        onConfirm: () => router.push(routeInfo.path),
      });
    } else if (nextStep === '낭만분석') {
      showConfirm({
        title: '구매가 완료되었습니다.',
        message,
        title2: '낭만분석으로 이동할까요?',
        title2Class: 'confirm-highlight-orange',
        text: '이동',
        onConfirm: () => router.push('/saju-service'),
      });
    } else if (nextStep === '사주입력') {
      showConfirm({
        title: '구매가 완료되었습니다.',
        message,
        title2: '사주입력으로 이동할까요?',
        title2Class: 'confirm-highlight-orange',
        text: '이동',
        onConfirm: () => router.push('/saju-input'),
      });
    }
  } catch {
    // next-step API 실패 시 기존 로직 fallback
    if (!primarySajuStore.primarySajuId) {
      await primarySajuStore.fetchPrimarySaju();
    }

    const routeInfo = getRouteByAnalysisTypeId(analysisTypeId, {
      primarySajuId: primarySajuStore.primarySajuId,
    });
    if (!routeInfo) return;

    showConfirm({
      title: '구매가 완료되었습니다.',
      title2: `${routeInfo.label} 페이지로 이동하시겠습니까?`,
      title2Class: 'confirm-highlight-orange',
      text: '이동',
      onConfirm: () => router.push(routeInfo.path),
    });
  }
};

// =============== 게시물 링크 인터셉터 (v-html 내부 <a> 클릭 제어) ===============
// 목적: v-html로 렌더된 본문에서 링크 클릭을 가로채 다음으로 처리
//   - 내부 링크: router.push 로 라우팅
//   - 웹: 내부 경로만 router.push 로 처리

/** v-html 컨테이너 ref */
const articleRef = ref(null);

const sanitizeAnchors = () => {
  const root = articleRef.value;
  if (!root) return;
  const links = root.querySelectorAll('a') || [];
  links.forEach((link) => {
    link.removeAttribute('target');
    link.setAttribute('rel', 'noopener');
    // 필요시 inline JS 제거
    // ;[...link.attributes]
    //   .filter(attr => attr.name.toLowerCase().startsWith('on'))
    //   .forEach(attr => link.removeAttribute(attr.name))
  });
};

/** v-html 영역에 클릭 이벤트 위임(캡처 단계 true로 등록: 기본 네비보다 먼저 가로챔) */
const attachLinkInterceptor = () => {
  if (!articleRef.value) return;
  articleRef.value.addEventListener('click', onArticleLinkClick, true);
};

/** 중복 등록/메모리 릭 방지를 위한 해제 */
const detachLinkInterceptor = () => {
  if (!articleRef.value) return;
  articleRef.value.removeEventListener('click', onArticleLinkClick, true);
};

const onArticleLinkClick = (e) => {
  const anchor = e.target?.closest?.('a');
  if (!anchor) return;

  const href = anchor.getAttribute('href');
  if (!href || href.startsWith('javascript:')) return;

  const { VITE_WEB_BASE_URL } = import.meta.env;
  const devUrl = 'https://d25web.thegam.io';

  // 내부 링크 → router.push, 외부 링크 → 기본 동작
  const pathname = getPathnameFromHref(href, { base: VITE_WEB_BASE_URL, extraHosts: [devUrl] });
  if (pathname) {
    e.preventDefault();
    e.stopPropagation();
    router.push(pathname);
  }
};

const refreshArticleLinks = () => {
  sanitizeAnchors();
  detachLinkInterceptor();
  attachLinkInterceptor();
};

onMounted(async () => {
  await nextTick();
  refreshArticleLinks();
});

onUnmounted(() => {
  detachLinkInterceptor();
  window.removeEventListener('resize', handleResize);
});

watch(articleHtml, async () => {
  await nextTick();
  refreshArticleLinks();
});
</script>

<template>
  <div class="my-info-wrap purchase-ticket-detail">
    <div class="my-tit-box com-bg-my-box h-small st-guide com-pc-title-box">
      <div class="tit">분석권 안내</div>
    </div>
    <div class="com-app-tit-box">
      <div class="btn-left">
        <button class="btn-back" @click="goViewList()">
          <IconArrow03 />
        </button>
      </div>
      <div class="tit">낭만분석권 구매</div>
      <div class="btn-box">
        <button class="txt-like" @click="patchLike()">
          <IconLike class="icon-like" :class="{ active: likeData.likeClicked }" />
        </button>
      </div>
    </div>
    <div class="myinfo-box com-bg-my-box">
      <div class="guide-tab-content-box">
        <div class="guide-pro-top">
          <button class="txt-like display-pc-only" @click="patchLike()">
            <IconLike class="icon-like" :class="{ active: likeData.likeClicked }" />
          </button>
          <div class="img-box" v-if="ticketData?.thumbnail">
            <img :src="ticketData?.thumbnail?.publishedUri" alt="" />
          </div>
          <div v-else class="none-thumbnail"><div class="img"></div></div>
          <div class="text-box">
            <div class="txt1">
              <span class="txt1-1">{{ ticketData?.boardCategory?.categoryCtn }}</span>
              <div v-if="ticketData?.icon" class="art-icon-box">
                <span
                  v-for="(icon, iconIdx) in ticketData.icon.split(',')"
                  :key="iconIdx"
                  class="txt"
                  :class="icon"
                >
                  {{ icon === 'RECO' ? '추천' : icon }}
                </span>
              </div>
            </div>
            <div class="txt2">{{ ticketData?.articleTtl }}</div>
            <div
              class="txt3"
              v-html="
                ticketData?.articleTtlSub
                  ?.replaceAll('\\n', '<br>')
                  .replaceAll('\n', '<br>')
                  .replace(/^(<br\s*\/?>)+/, '')
                  .replace(/(<br\s*\/?>)+$/, '')
              "
            ></div>
            <div class="txt4">
              <span v-for="(keyword, index) in keywords" :key="index">#{{ keyword }}</span>
            </div>

            <div class="txt5">
              <div v-if="!isOptionEmpty" class="type-box">
                <div ref="selectWrapperRef" class="custom-select-wrapper" @click="toggleDropdown">
                  <span> {{ selectedOptionLabel }}</span>
                  <div class="dropdown-icon" :class="{ open: showDropdown }"></div>
                  <ul v-show="showDropdown" class="dropdown-list">
                    <li
                      v-for="option in optionItems"
                      :key="option.value"
                      @click.stop="selectOption(option)"
                      class="dropdown-item"
                      :class="['dropdown-item', { disabled: option.disabled }]"
                    >
                      {{ option.title }}
                    </li>
                  </ul>
                </div>
              </div>

              <div class="price-action-row">
                <div class="price-box">
                  <span class="pirce1" v-show="discountRate > 0"
                    >{{ formatNumber(totalBasePrice) }} P</span
                  >
                  <div class="discount-box">
                    <span v-show="discountRate > 0" class=""
                      ><span class="pirce2"> {{ discountRate }}%</span></span
                    >
                    <span class="pirce3">{{ formatNumber(totalPrice) }} P</span>
                  </div>
                </div>

                <button
                  class="btn-payment-big"
                  @click="openPurchasePopup"
                  :disabled="!isPurchaseEnabled"
                >
                  구매하기
                </button>

                <button
                  v-if="ticketData?.product?.isGiftable"
                  class="btn-gifting"
                  @click="openGiftingPopup"
                  :disabled="!isPurchaseEnabled"
                >
                  <component :is="IconGift" class="btn-gifting__icon" />
                  <span>선물</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 상품 가이드 섹션 (상단) -->
      <TicketProductGuide v-if="topGuideConfig" :guideConfig="topGuideConfig" />

      <div class="tab-guide-list">
        <button class="btn-tab-guide active" @click="scrollToSection(tab.value)">상세설명</button>
      </div>

      <!-- 상품 가이드 섹션 (상세설명 아래 - 낭만궁합 1:1 전용) -->
      <TicketProductGuide v-if="bottomGuideConfig" :guideConfig="bottomGuideConfig" />

      <div class="guide-pro-cont">
        <div ref="articleRef" class="article-content-box product" v-html="articleHtml" />
      </div>
      <section class="btn-com gap-xl center guide-detail-btn-box">
        <button
          v-if="prevData && prevData.previousId"
          @click="goViewPrevNext(prevData.previousBoardId, prevData.previousId)"
          class="btnPrev"
        >
          <IconArrow03 />
          <div class="txt-box">
            <span class="txt1">{{ prevData.previousTitle }}</span>
            <span class="txt2">이전 글 보기</span>
          </div>
        </button>
        <button v-else class="disabled-button btnPrev">
          <IconArrow03 />
          <div class="txt-box">
            <span class="txt1">이전 글 없음</span>
            <span class="txt2">이전 글 보기</span>
          </div>
        </button>
        <button class="btn-line w-large bg-w btnList" @click="goViewList()">목록으로</button>
        <button
          v-if="prevData && prevData.nextId"
          @click="goViewPrevNext(prevData.nextBoardId, prevData.nextId)"
          class="btnNext"
        >
          <div class="txt-box">
            <span class="txt1">{{ prevData.nextTitle }}</span>
            <span class="txt2">다음 글 보기</span>
          </div>
          <IconArrow04 />
        </button>
        <button v-else class="disabled-button btnNext">
          <div class="txt-box">
            <span class="txt1">다음 글 없음</span>
            <span class="txt2">다음 글 보기</span>
          </div>
          <IconArrow04 />
        </button>
      </section>
    </div>
  </div>

  <!-- 구매 팝업 -->
  <PopPurchase
    ref="purchasePopupRef"
    :ticketData="ticketData"
    :optionItems="optionItems"
    :selectedOption="selectedOptionData"
    :totalPrice="totalPrice"
    :totalBasePrice="totalBasePrice"
    :accessToken="accessToken"
    @purchase-success="onPurchaseSuccess"
  />

  <!-- 선물하기 팝업 -->
  <PopGifting
    ref="giftingPopupRef"
    :ticketData="ticketData"
    :optionItems="optionItems"
    :selectedOption="selectedOptionData"
    :totalPrice="totalPrice"
    :totalBasePrice="totalBasePrice"
    :accessToken="accessToken"
  />

  <ConfirmComponent
    ref="confirmRef"
    :title="dialogTitle"
    :titleClass="dialogTitleClass"
    :title2="dialogTitle2"
    :title2Class="dialogTitle2Class"
    :message="dialogMessage"
    :text="dialogText"
    :nextUrl="dialogNextUrl"
    :warning="dialogWarning"
  />
</template>

<style lang="scss">
@import '@/assets/nangman_style/product.scss';
@import '@/assets/webview/guide_product.scss';

.price-action-row {
  width: 100%;
  min-height: 52px;
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
}

.btn-payment-big {
  display: flex;
  width: 100px;
  padding: 10px 16px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 6px;
  background: var(--orange-300-main, #eb4c10);
  color: var(--gray-100-white, #fff);
  text-align: center;
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 18px;

  &:disabled {
    background: var(--Gray-500, #949494);
    color: var(--gray-100-white, #fff);
    cursor: not-allowed;
  }
}
.btn-gifting {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  color: #eb4c10;
  text-align: center;
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px;
  .btn-gifting__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
  }
}

/*
.line-box {
  height: 1px;
  background-color: var(--Gray-300, #e6e6e6);
  margin: 8px 0;
  pointer-events: none;
}
  */

.custom-select-wrapper {
  display: flex;
  width: 200px;
  height: 38px;
  padding: 10px 12px;
  justify-content: space-between;
  align-items: center;
  border-radius: 8px;
  border: 1px solid var(--Gray-300, #ebebeb);
  background: var(--gray-100-white, #fff);
  cursor: pointer;
  position: relative;
  color: var(--Gray-800, #333);
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px;
}
.dropdown-icon {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  margin-left: 8px;
  background: url('@manseImages/dropdown-icon.svg') center center no-repeat;
  background-size: contain;
  transition: transform 0.3s ease;
}
.dropdown-icon.open {
  transform: rotate(180deg);
  transition: transform 0.3s ease;
}
.dropdown-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid var(--Gray-300, #ebebeb);
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  z-index: 100;
  list-style: none;
  padding: 6px 0;
  max-height: none;
  overflow-y: visible;
  animation: slideDown 0.3s ease-out;
  transform-origin: top center;
}
.dropdown-item {
  padding: 10px 16px;
  cursor: pointer;
  white-space: nowrap;
}
.dropdown-item:hover {
  background-color: #f5f5f5;
}
.dropdown-item.disabled {
  color: #aaa;
  cursor: default;
  pointer-events: none;
}
</style>
